import { COMMON_CONSTANTS, NeoTaxEnvironment } from '@common/neo.tax';
import * as FullStory from '@fullstory/browser';
import * as Sentry from '@sentry/browser';
import SentryFullStory from '@sentry/fullstory';
import { ErrorBoundary, Scope, captureException, init, setUser, setTag } from '@sentry/react';
import { ComponentType, ReactElement } from 'react';

import { LocalStorageKey } from '@neo.tax/shared/enums';
import { IdentifiableUser } from '@neo.tax/shared/initialization';
import { AppWideError } from '@neo.tax/shared/organisms/app-wide-error';
import { StringBoolean, env } from '@neo.tax/shared/utils';

import { isVpcscCheckFailedError } from '../firebase/vpcsc.utils';

/**
 * Previews report under `development` so their errors reach the same Sentry alerts as the
 * development deployment.
 */
export const toSentryEnvironment = (neoTaxEnvironment: NeoTaxEnvironment): string => {
  switch (neoTaxEnvironment) {
    case NeoTaxEnvironment.DEVELOPMENT:
    case NeoTaxEnvironment.DEPLOY_PREVIEW:
    case NeoTaxEnvironment.PRODUCTION_PREVIEW:
      return NeoTaxEnvironment.DEVELOPMENT;
    default:
      return neoTaxEnvironment;
  }
};

/**
 * Files every VPC Service Controls refusal, however it was wrapped, as information in a single issue: a network
 * refusing Identity Platform is an expected outcome rather than a fault, but its rate is still worth seeing.
 */
export const downgradeVpcscRefusal = (event: Sentry.Event, hint: Sentry.EventHint): Sentry.Event =>
  isVpcscCheckFailedError(hint.originalException)
    ? { ...event, level: 'info', fingerprint: ['vpcsc-check-failed'] }
    : event;

export const initializeSentry = (): void => {
  init({
    beforeSend: downgradeVpcscRefusal,
    dsn: env.REACT_APP_SENTRY_DSN,
    environment: toSentryEnvironment(env.REACT_APP_NEO_TAX_ENV),
    integrations: [
      new Sentry.BrowserTracing(),
      new SentryFullStory(COMMON_CONSTANTS.SENTRY_ORG_SLUG, { client: FullStory }),
    ],
    release: env.REACT_APP_SENTRY_RELEASE,
    // ignore false-positive Sentry reports that are also missing stacktrace
    // https://github.com/getsentry/sentry-javascript/issues/3440
    ignoreErrors: ['Non-Error promise rejection captured'],
  });
};

export const identifySentryUser = (identifiableUser: IdentifiableUser): void => {
  const { email, firebaseUserId, fullName, isSuperUserMode } = identifiableUser;
  setUser({
    email,
    id: firebaseUserId,
  });
  setTag('user-email', email);
  setTag('user-name', fullName);
  setTag('super-user-mode', isSuperUserMode);
  // company-name is set when useCompany() hook fetches company data
};

export const wrapWithSentryProvider = (
  Component: ComponentType<Record<string, unknown>>,
): ComponentType<Record<string, unknown>> => {
  const WrappedComponent = (props: Record<string, unknown>): ReactElement => (
    <ErrorBoundary
      beforeCapture={(scope: Scope): void => {
        try {
          const appReloadOnError =
            localStorage.getItem(LocalStorageKey.SESSION_ON_ERROR_RELOAD_STATE) ?? StringBoolean.FALSE;
          scope.setContext('local-storage', { ...localStorage });

          if (appReloadOnError === StringBoolean.FALSE) {
            localStorage.setItem(LocalStorageKey.SESSION_ON_ERROR_RELOAD_STATE, StringBoolean.TRUE);
          } else {
            localStorage.setItem(LocalStorageKey.SESSION_ON_ERROR_RELOAD_STATE, StringBoolean.FALSE);
          }
        } catch (error) {
          // If we get an error attempting to interact with local-storage, we catch and report the error
          // but we do not want to throw in the error boundary
          captureException(error, { extra: { source: 'ErrorBoundary::beforeCapture' } });
        }
      }}
      fallback={AppWideError}
    >
      <Component {...props} />
    </ErrorBoundary>
  );

  // see react/display-name eslint rule
  WrappedComponent.displayName = `SentryProviderWrapped${Component.displayName ?? Component.name}`;
  return WrappedComponent;
};
